import {FC, PropsWithChildren, ReactNode, useState} from 'react';
import {useLocation} from 'react-router-dom';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {Box, Button, Stack, Tooltip} from '@mui/material';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';

import {activeBgColor, activeColor} from './constants';

import {COLOR__MAIN_BLACK, COLOR__WHITE} from '@/theme/colors';

interface Props {
  groupRoutes: string[];
  text: string;
  icon?: ReactNode;
  defaultOpen?: boolean;
  isCollapsed?: boolean;
  onExpandSidebar?: () => void;
}

export const SubMenu: FC<PropsWithChildren<Props>> = ({
  children,
  text,
  icon,
  groupRoutes,
  defaultOpen = false,
  isCollapsed = false,
  onExpandSidebar,
}) => {
  const {pathname} = useLocation();
  const isActive = groupRoutes.some(r => pathname.startsWith(r));
  const [open, setOpen] = useState(isActive || defaultOpen);

  const handleClick = () => {
    // Clicking an icon of the collapsed sidebar unfolds it on this section
    if (isCollapsed) {
      onExpandSidebar?.();
      setOpen(true);
      return;
    }

    setOpen(!open);
  };

  return (
    <>
      <Tooltip title={isCollapsed ? text : ''} placement="right">
        <Button
          onClick={handleClick}
          variant="outlined"
          sx={{
            padding: isCollapsed ? '12px' : '12px 16px',
            minWidth: isCollapsed ? 0 : undefined,
            justifyContent: isCollapsed ? 'center' : 'flex-start',

            backgroundColor: isActive ? activeBgColor : COLOR__WHITE,

            '& svg': {
              color: isActive ? activeColor : COLOR__MAIN_BLACK,
              width: 24,
              height: 'auto',
            },

            border: '1px solid transparent',
            '&:hover': {
              backgroundColor: activeBgColor,
              border: '1px solid transparent',
            },
            '&:active': {
              backgroundColor: activeBgColor,
              border: '1px solid transparent',
            },
          }}
        >
          <Box
            display="flex"
            gap={1}
            alignItems="center"
            width={isCollapsed ? 'auto' : '100%'}
          >
            {icon}

            {!isCollapsed && (
              <>
                <Typography
                  sx={{
                    fontSize: 16,
                    lineHeight: '20px',
                    color: isActive ? activeColor : COLOR__MAIN_BLACK,
                    fontWeight: isActive ? 500 : 400,
                  }}
                >
                  {text}
                </Typography>

                {open ? (
                  <ExpandLess sx={{marginLeft: 'auto'}} />
                ) : (
                  <ExpandMore sx={{marginLeft: 'auto'}} />
                )}
              </>
            )}
          </Box>
        </Button>
      </Tooltip>

      <Collapse in={open && !isCollapsed} timeout="auto" unmountOnExit>
        <Stack spacing={2} paddingLeft={2.5} component="nav">
          {children}
        </Stack>
      </Collapse>
    </>
  );
};
